"use client";

import React, { Suspense } from "react";
import { useHomepageData } from "@/components/homepage/hooks/use-homepage-data";
import type { HomepageArticle } from "@/components/homepage/types";
import { BreakingNewsSection } from "@/components/homepage/sections/breaking-news";
import { EditorialHeroSection } from "@/components/homepage/sections/editorial-hero";
import { TodayHighlightsSection } from "@/components/homepage/sections/today-highlights";
import { TrendingNewsSection } from "@/components/homepage/sections/trending-news";
import { FeaturedInvestigationSection } from "@/components/homepage/sections/featured-investigation";
import { EditorsPicksSection } from "@/components/homepage/sections/editors-picks";
import { ResearchSpotlightSection } from "@/components/homepage/sections/research-spotlight";
import { LatestArticlesSection } from "@/components/homepage/sections/latest-articles";
import { FeaturedBlogsSection } from "@/components/homepage/sections/featured-blogs";
import { ExpertOpinionsSection } from "@/components/homepage/sections/expert-opinions";
import { WhitepapersSection } from "@/components/homepage/sections/whitepapers";
import { CaseStudiesSection } from "@/components/homepage/sections/case-studies";
import { CommunityHighlightsSection } from "@/components/homepage/sections/community-highlights";
import { TrendingDiscussionsSection } from "@/components/homepage/sections/trending-discussions";
import { QuestionsForExpertsSection } from "@/components/homepage/sections/questions-for-experts";
import { FeaturedOrganizationsSection } from "@/components/homepage/sections/featured-organizations";
import { LeadershipInsightsSection } from "@/components/homepage/sections/leadership-insights";
import { LearningSpotlightSection } from "@/components/homepage/sections/learning-spotlight";
import { CoursesSection } from "@/components/homepage/sections/courses-section";
import { UpcomingEventsSection } from "@/components/homepage/sections/upcoming-events";
import { CuratedSection } from "@/components/homepage/sections/curated-section";
import { CareerOpportunitiesSection } from "@/components/homepage/sections/career-opportunities";
import { MentorshipSection } from "@/components/homepage/sections/mentorship";
import { WeeklyDigestSection } from "@/components/homepage/sections/weekly-digest";
import { NewsletterSection } from "@/components/homepage/sections/newsletter-section";
import { SponsoredContentSection } from "@/components/homepage/sections/sponsored-content-section";
import { AppDownloadSection } from "@/components/homepage/sections/app-download-section";

function SectionWrapper({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export default function HomePage() {
  const {
    news,
    articles,
    events,
    courses,
    communities,
    jobs,
    reports,
    discussions,
    organizations,
    qaItems,
    mentors,
    complianceAlerts,
    isLoading,
  } = useHomepageData();

  const featured = articles[0] ?? null;
  const trending = articles.slice(1, 4);
  const highlights = articles.slice(0, 6);
  const investigation = articles[1] ?? null;
  const picks = articles.slice(2, 6);
  const research = reports.slice(0, 4);
  const whitepapers = reports.slice(0, 4);
  const caseStudies = reports.slice(0, 4);
  const blogs = articles.slice(0, 4);
  const experts = articles.slice(0, 4);
  const activeDiscussion = discussions[0] ?? null;
  const trendingDiscussions = discussions.slice(0, 4);
  const questions = qaItems.slice(0, 4);
  const orgs = organizations.slice(0, 4);
  const learningPaths = courses.slice(0, 4);
  const certs = courses.slice(0, 4);
  const upcomingEvents = events.slice(0, 4);
  const hrTech = news.slice(0, 4);
  const aiHr = news.slice(0, 4);
  const recruitment = jobs.slice(0, 4);
  const compliance = complianceAlerts.slice(0, 4);
  const policy = news.slice(0, 4);
  const mentorProfiles = mentors.slice(0, 4);
  const sponsored = news.slice(0, 2);

  return (
    <main className="min-h-screen">
      <SectionWrapper>
        <BreakingNewsSection items={news.slice(0, 5)} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <EditorialHeroSection
          featured={featured}
          trendingTopics={[
            { label: "AI in HR", href: "/ai-search" },
            { label: "People Analytics", href: "/knowledge-hub" },
            { label: "DEI", href: "/community" },
            { label: "HR Tech", href: "/articles" },
            { label: "Talent Acquisition", href: "/career" },
            { label: "Compliance", href: "/knowledge-hub" },
            { label: "Employee Experience", href: "/community" },
            { label: "Future of Work", href: "/articles" },
          ]}
          popularTags={[
            "HR Strategy", "Leadership", "Employee Retention", "Workplace Culture",
            "Compensation", "Performance Management", "Learning & Development",
            "Diversity & Inclusion", "HR Technology", "Recruitment", "People Analytics",
            "Employee Engagement", "Organizational Design", "Change Management", "Employee Wellbeing",
          ]}
          isLoading={isLoading}
        />
      </SectionWrapper>

      <SectionWrapper>
        <TodayHighlightsSection items={highlights} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <TrendingNewsSection featured={featured} secondary={trending} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <FeaturedInvestigationSection article={investigation} related={articles.slice(2, 5)} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <EditorsPicksSection items={picks} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <ResearchSpotlightSection items={research} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <LatestArticlesSection items={articles.slice(0, 8)} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <FeaturedBlogsSection items={blogs} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <ExpertOpinionsSection items={experts} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <WhitepapersSection items={whitepapers} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CaseStudiesSection items={caseStudies} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CommunityHighlightsSection items={communities} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <TrendingDiscussionsSection activeDiscussion={activeDiscussion} discussions={trendingDiscussions} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <QuestionsForExpertsSection items={questions} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <FeaturedOrganizationsSection items={orgs} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <LeadershipInsightsSection items={experts} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <LearningSpotlightSection items={learningPaths} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CoursesSection items={courses} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <UpcomingEventsSection items={upcomingEvents} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CuratedSection title="HR Tech" icon={Code2Icon} items={hrTech} href="/articles" isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CuratedSection title="AI in HR" icon={BrainIcon} items={aiHr} href="/ai-search" isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CuratedSection title="Recruitment Intelligence" icon={BriefcaseIcon} items={recruitment} href="/career" isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CuratedSection title="Compliance Updates" icon={ShieldCheckIcon} items={compliance} href="/compliance" isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CuratedSection title="Policy Changes" icon={ScaleIcon} items={policy} href="/policy-updates" isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <CareerOpportunitiesSection items={jobs} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <MentorshipSection items={mentorProfiles} isLoading={isLoading} />
      </SectionWrapper>

      <SectionWrapper>
        <WeeklyDigestSection />
      </SectionWrapper>

      <SectionWrapper>
        <NewsletterSection />
      </SectionWrapper>

      <SectionWrapper>
        <SponsoredContentSection items={sponsored} />
      </SectionWrapper>

      <SectionWrapper>
        <AppDownloadSection />
      </SectionWrapper>
    </main>
  );
}

function Code2Icon(props: React.SVGProps<SVGSVGElement>) { return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>; }
function BrainIcon(props: React.SVGProps<SVGSVGElement>) { return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M12 2a8 8 0 0 0-8 8c0 3.4 2.1 6.3 5 7.5V20h6v-2.5c2.9-1.2 5-4.1 5-7.5a8 8 0 0 0-8-8Z"/><path d="M9 20v1a6 6 0 0 0 6 0v-1"/></svg>; }
function BriefcaseIcon(props: React.SVGProps<SVGSVGElement>) { return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>; }
function ShieldCheckIcon(props: React.SVGProps<SVGSVGElement>) { return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></svg>; }
function ScaleIcon(props: React.SVGProps<SVGSVGElement>) { return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M16 3h5v5"/><path d="M4 3h5v5H4z"/><path d="M16 19h5v5h-5z"/><path d="M4 19h5v5H4z"/><path d="M16 8h5v8H4V8h5z"/></svg>; }